import shpwrite from '@mapbox/shp-write'

import type { AnaliseAdicionada } from '../../../../../types'
import { montarNomeArquivoKml } from '../../../meta'
import { errorAlert } from '../../../../../../../../../../../../../utils/alert'

/** ha para cada amostra (área da propriedade / área de amostra do laudo). */
export function calcularAmostraHa(area_propriedade: string | number, area_amostra_laudo: string): string {
    const ha = Number(area_propriedade)
    const am = parseFloat(area_amostra_laudo)
    if (!am || isNaN(ha) || isNaN(am) || am <= 0) return '0.00'
    const calc = ha / am
    if (isNaN(calc) || calc <= 0) return '0.00'
    const calcFixed = parseFloat(calc.toFixed(2))
    if (!calcFixed) return '0.00'
    const total = ha / calcFixed
    return isFinite(total) ? total.toFixed(2) : '0.00'
}

/** Divide área (string) pela quantidade de pontos, com proteções. */
export function haPorPonto(area: string, listaPontos: any[]): string {
    const areaStr = String(area ?? '').replace(/[^0-9.,]/g, '').replace(',', '.')
    const areaNum = Number(areaStr)
    const qtd = listaPontos?.length || 0
    if (isNaN(areaNum) || areaNum <= 0 || qtd <= 0) return '0.00'
    const r = areaNum / qtd
    return isFinite(r) ? r.toFixed(2) : '0.00'
}

/**
 * Gera e baixa um KML com os pontos de uma análise.
 * O arquivo sai como propriedade_talhao_analise_profundidade.kml.
 */
export function baixarKmlAnalise(item: AnaliseAdicionada, nomePropriedade?: string, nomeTalhao?: string) {
    const pontos = item?.pontos_selecionados || []
    const nomeAnalise = item?.analise_nome || 'analise'
    const profundidade = item?.profundidade || ''

    const nomeArquivo = montarNomeArquivoKml(
        [nomePropriedade, nomeTalhao, nomeAnalise, profundidade],
        'analise'
    )

    const placemarks = pontos
        .map((p) => {
            const nome = p?.NOME?.toString().padStart(2, '0') || ''
            const lat = p?.LATITUDE || 0
            const lng = p?.LONGITUDE || 0
            return `    <Placemark>
      <name>${nome}</name>
      <description>Análise: ${nomeAnalise} | Profundidade: ${profundidade}</description>
      <Point><coordinates>${lng},${lat},0</coordinates></Point>
    </Placemark>`
        })
        .join('\n')

    const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${nomeAnalise} - ${profundidade}</name>
${placemarks}
  </Document>
</kml>`

    const blob = new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${nomeArquivo}.kml`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

/**
 * Gera e baixa os pontos de uma análise como Shapefile (.zip).
 * Mesmo nome-base do KML; o shp-write empacota .shp/.shx/.dbf/.prj num zip.
 */
export function baixarShapeAnalise(item: AnaliseAdicionada, nomePropriedade?: string, nomeTalhao?: string) {
    const pontos = item?.pontos_selecionados || []

    if (pontos.length === 0) {
        return errorAlert('Nenhum ponto para converter em shape.')
    }

    const nomeAnalise = item?.analise_nome || 'analise'
    const profundidade = item?.profundidade || ''

    const nomeArquivo = montarNomeArquivoKml(
        [nomePropriedade, nomeTalhao, nomeAnalise, profundidade],
        'analise'
    )

    const geojson = {
        type: 'FeatureCollection' as const,
        features: pontos.map((ponto, index) => ({
            type: 'Feature' as const,
            properties: {
                INDICE: ponto?.INDICE ?? index + 1,
                NOME: ponto?.NOME ?? '',
                ANALISE: nomeAnalise,
                PROFUND: profundidade,
                LATITUDE: ponto?.LATITUDE ?? 0,
                LONGITUDE: ponto?.LONGITUDE ?? 0,
            },
            geometry: {
                type: 'Point' as const,
                coordinates: [ponto?.LONGITUDE ?? 0, ponto?.LATITUDE ?? 0],
            },
        })),
    }

    shpwrite
        .zip(geojson, { outputType: 'blob', compression: 'DEFLATE' })
        .then((resultado) => {
            const url = URL.createObjectURL(resultado as Blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `${nomeArquivo}.zip`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        })
        .catch(() => {
            errorAlert('Erro ao converter os pontos para shape.')
        })
}
